import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Trash2, Wand2, Download, Eye } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function InputSchemaEditor({ agent, userToken, onSchemaUpdate, isCreationMode = false, onSchemaChange }) {
  const [activeTab, setActiveTab] = useState('visual');
  const [aiDescription, setAiDescription] = useState('');
  
  // Get token from localStorage if not provided via props
  const token = userToken || localStorage.getItem('token');
  
  // Debug: Log component props
  React.useEffect(() => {
    console.log('[InputSchemaEditor] Component mounted/updated with:', {
      agentId: agent?.id,
      agentName: agent?.name,
      isCreationMode,
      hasToken: !!token,
      apiBaseUrl: API_BASE_URL
    });
  }, [agent?.id, isCreationMode, token]);
  
  // Parse input_schema if it's a string
  const getInitialFields = () => {
    if (!agent.input_schema) return [];
    
    let schema = agent.input_schema;
    
    // If it's a string, parse it first
    if (typeof schema === 'string') {
      try {
        schema = JSON.parse(schema);
      } catch (e) {
        console.error('[InputSchemaEditor] Error parsing input_schema string:', e);
        return [];
      }
    }
    
    // If it's an object with fields array (new format)
    if (typeof schema === 'object' && Array.isArray(schema.fields)) {
      return schema.fields;
    }
    
    // If it's an object in old format like {"query": "string", "parameters": "object"}
    // Convert it to new format
    if (typeof schema === 'object' && !Array.isArray(schema.fields)) {
      console.log('[InputSchemaEditor] Converting old schema format to new format');
      const fields = Object.entries(schema).map(([name, type]) => ({
        name,
        type: typeof type === 'string' ? type : 'string',
        description: `Campo ${name}`,
        example: '',
        required: true
      }));
      return fields;
    }
    
    return [];
  };
  
  const [schemaFields, setSchemaFields] = useState(getInitialFields());
  
  const queryClient = useQueryClient();
  
  // Update fields when agent.input_schema changes (e.g., loaded from backend)
  React.useEffect(() => {
    console.log('[InputSchemaEditor] useEffect triggered, agent.input_schema:', agent.input_schema, 'type:', typeof agent.input_schema);
    
    if (!agent.input_schema) {
      console.log('[InputSchemaEditor] No input_schema, skipping');
      return;
    }
    
    let schema = agent.input_schema;
    
    // Parse string to object if needed
    if (typeof schema === 'string') {
      try {
        schema = JSON.parse(schema);
        console.log('[InputSchemaEditor] Parsed string to object:', schema);
      } catch (e) {
        console.error('[InputSchemaEditor] Error parsing input_schema string:', e, 'raw value:', agent.input_schema);
        return;
      }
    }
    
    let parsedFields = [];
    
    // New format: {fields: [{name, type, ...}, ...]}
    if (typeof schema === 'object' && Array.isArray(schema.fields)) {
      parsedFields = schema.fields;
      console.log('[InputSchemaEditor] Using new format, fields:', parsedFields);
    }
    // Old format: {field_name: "type", ...} - convert to new format
    else if (typeof schema === 'object') {
      console.log('[InputSchemaEditor] Converting old format to new format');
      parsedFields = Object.entries(schema).map(([name, type]) => ({
        name,
        type: typeof type === 'string' ? type : 'string',
        description: `Campo ${name}`,
        example: '',
        required: true
      }));
      console.log('[InputSchemaEditor] Converted fields:', parsedFields);
    }
    
    console.log('[InputSchemaEditor] Setting schemaFields to:', parsedFields, 'length:', parsedFields.length);
    setSchemaFields(parsedFields);
  }, [agent.input_schema]);
  
  // In creation mode, update parent component when schema changes
  const prevSchemaFieldsRef = React.useRef();
  React.useEffect(() => {
    if (isCreationMode && onSchemaChange && schemaFields.length > 0) {
      const prevFields = prevSchemaFieldsRef.current;
      if (JSON.stringify(prevFields) !== JSON.stringify(schemaFields)) {
        onSchemaChange(schemaFields);
        prevSchemaFieldsRef.current = schemaFields;
      }
    }
  }, [schemaFields, isCreationMode, onSchemaChange]);

  // Generate schema with AI
  const generateSchemaMutation = useMutation({
    mutationFn: async () => {
      if (isCreationMode) {
        // In creation mode, use preview endpoint that doesn't require agent_id
        const response = await axios.post(
          `${API_BASE_URL}/api/agents/schema/generate-preview`,
          null,
          { 
            params: {
              description: aiDescription,
              agent_name: agent.name || 'New Agent'
            },
            headers: { Authorization: `Bearer ${token}` } 
          }
        );
        return response.data;
      }
      const response = await axios.post(
        `${API_BASE_URL}/api/agents/${agent.id}/schema/generate`,
        { agent_id: agent.id, description: aiDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log('[InputSchemaEditor] AI response:', data);
      console.log('[InputSchemaEditor] Fields to set:', data.schema.fields);
      
      if (data.schema && data.schema.fields && Array.isArray(data.schema.fields)) {
        setSchemaFields(data.schema.fields);
        console.log('[InputSchemaEditor] Schema fields updated:', data.schema.fields.length, 'fields');
        
        // Switch to preview tab to show generated fields
        setTimeout(() => {
          setActiveTab('preview');
        }, 100);
        
        toast.success("Schema generado", {
          description: `${data.schema.fields.length} campos generados. Revisa el preview.`,
        });
        
        if (onSchemaUpdate) onSchemaUpdate();
      } else {
        console.error('[InputSchemaEditor] Invalid schema format:', data);
        toast.error("Error", {
          description: "Formato de schema inválido. Por favor intenta de nuevo.",
        });
      }
    },
    onError: (error) => {
      toast.error("Error al generar schema", {
        description: error.response?.data?.detail || error.message,
      });
    }
  });

  // Update schema
  const updateSchemaMutation = useMutation({
    mutationFn: async (schema) => {
      console.log('[updateSchemaMutation] Starting with:', {
        isCreationMode,
        schemaFieldsCount: schema.length,
        agentId: agent.id,
        url: `${API_BASE_URL}/api/agents/${agent.id}/schema`
      });
      
      if (isCreationMode) {
        // In creation mode, just update local state
        console.log('[updateSchemaMutation] In creation mode, skipping API call');
        return { success: true };
      }
      
      console.log('[updateSchemaMutation] Sending PUT request with fields:', schema);
      const response = await axios.put(
        `${API_BASE_URL}/api/agents/${agent.id}/schema`,
        { fields: schema },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[updateSchemaMutation] Response:', response.data);
      return response.data;
    },
    onSuccess: () => {
      if (isCreationMode) {
        toast.success("Schema configurado", {
          description: "El schema se guardará al crear el agente.",
        });
      } else {
        toast.success("¡Schema guardado exitosamente!", {
          description: "Los cambios se han guardado correctamente.",
        });
        queryClient.invalidateQueries(['modules']);
      }
      if (onSchemaUpdate) onSchemaUpdate();
    },
    onError: (error) => {
      toast.error("Error al guardar schema", {
        description: error.response?.data?.detail || "No se pudo guardar el schema. Intenta de nuevo.",
      });
    }
  });

  const addField = () => {
    setSchemaFields([
      ...schemaFields,
      { name: '', type: 'text', description: '', example: '', required: true }
    ]);
  };

  const removeField = (index) => {
    setSchemaFields(schemaFields.filter((_, i) => i !== index));
  };

  const updateField = (index, key, value) => {
    const updated = [...schemaFields];
    updated[index] = { ...updated[index], [key]: value };
    setSchemaFields(updated);
  };

  const saveSchema = () => {
    console.log('[saveSchema] Called with:', {
      isCreationMode,
      schemaFieldsCount: schemaFields.length,
      schemaFields,
      agentId: agent.id
    });
    
    // Validate agent.id
    if (!agent.id) {
      console.error('[saveSchema] agent.id is undefined or null');
      toast.error("Error", {
        description: "No se pudo identificar el agente. Por favor intenta de nuevo.",
      });
      return;
    }
    
    // In creation mode, also update parent component
    if (isCreationMode && onSchemaChange) {
      onSchemaChange(schemaFields);
    }
    
    console.log('[saveSchema] Calling updateSchemaMutation.mutate');
    updateSchemaMutation.mutate(schemaFields);
  };

  const downloadExcelTemplate = () => {
    // Create CSV content
    const headers = schemaFields.map(f => f.name).join(',');
    const examples = schemaFields.map(f => f.example || '').join(',');
    const csvContent = `${headers}\n${examples}`;
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agent.name.replace(/\s/g, '_')}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {isCreationMode && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
          <p className="text-xs text-blue-300">
            ℹ️ Configura el schema del agente. Usa <strong>Generación con IA</strong> para crear automáticamente basado en descripción, o el <strong>Editor Visual</strong> para configurar manualmente. El schema se guardará al crear el agente.
          </p>
        </div>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="visual">Editor Visual</TabsTrigger>
          <TabsTrigger value="ai">Generación con IA</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="h-5 w-5" />
                Generar Schema con IA
              </CardTitle>
              <CardDescription>
                Describe en lenguaje natural qué hace el agente y la IA generará automáticamente el schema de datos necesario.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Ej: Este agente analiza datos de ventas mensuales. Necesita información sobre fecha, producto vendido, cantidad, precio unitario, total de venta y nombre del vendedor."
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                rows={6}
              />
              <Button 
                onClick={() => generateSchemaMutation.mutate()}
                disabled={!aiDescription || generateSchemaMutation.isPending}
              >
                {generateSchemaMutation.isPending ? 'Generando...' : 'Generar Schema'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visual" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurar Campos del Schema</CardTitle>
              <CardDescription>Define los campos que el Agente 00 necesitará para este agente</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {schemaFields.map((field, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Campo {index + 1}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeField(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Nombre del Campo</label>
                      <Input
                        value={field.name}
                        onChange={(e) => updateField(index, 'name', e.target.value)}
                        placeholder="ej: fecha_venta"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Tipo de Dato</label>
                      <Select
                        value={field.type}
                        onValueChange={(value) => updateField(index, 'type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="number">Número</SelectItem>
                          <SelectItem value="date">Fecha</SelectItem>
                          <SelectItem value="boolean">Booleano</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Descripción</label>
                    <Input
                      value={field.description}
                      onChange={(e) => updateField(index, 'description', e.target.value)}
                      placeholder="Descripción del campo"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Ejemplo</label>
                    <Input
                      value={field.example || ''}
                      onChange={(e) => updateField(index, 'example', e.target.value)}
                      placeholder="Valor de ejemplo"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(index, 'required', e.target.checked)}
                      id={`required-${index}`}
                    />
                    <label htmlFor={`required-${index}`} className="text-sm">
                      Campo requerido
                    </label>
                  </div>
                </div>
              ))}

              <Button onClick={addField} variant="outline" className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Agregar Campo
              </Button>

              <Button onClick={saveSchema} className="w-full" disabled={updateSchemaMutation.isPending}>
                {updateSchemaMutation.isPending ? 'Guardando...' : 'Guardar Schema'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Preview del Schema
              </CardTitle>
              <CardDescription>Vista previa de la estructura esperada de datos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                console.log('[Preview] schemaFields:', schemaFields, 'length:', schemaFields.length);
                return null;
              })()}
              {schemaFields.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No hay campos definidos. Usa las otras pestañas para crear el schema.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border">
                      <thead>
                        <tr className="bg-muted">
                          {schemaFields.map((field, index) => (
                            <th key={index} className="border p-2 text-left">
                              {field.name}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {schemaFields.map((field, index) => (
                            <td key={index} className="border p-2 text-sm text-muted-foreground">
                              {field.example || `(${field.type})`}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-2 mt-4">
                    <h4 className="font-semibold">Detalles de Campos:</h4>
                    {schemaFields.map((field, index) => (
                      <div key={index} className="text-sm p-3 bg-muted rounded">
                        <span className="font-medium">{field.name}</span>
                        {' - '}
                        <span className="text-muted-foreground">
                          {field.description} ({field.type})
                        </span>
                      </div>
                    ))}
                  </div>

                  <Button onClick={downloadExcelTemplate} variant="outline" className="w-full">
                    <Download className="mr-2 h-4 w-4" />
                    Descargar Template Excel
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default InputSchemaEditor;
